<script setup lang="ts">
import { vElementVisibility } from '@vueuse/components'
import { omit, pick } from 'lodash-es'
import { NGrid, NGridItem, type DropdownOption } from 'naive-ui'
import { computed, h, inject, toRefs, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import EditableComponent from '@canvas-builder/components/container/EditableComponent/EditableComponent.vue'
import Carousel from '@canvas-builder/components/ui/Carousel/Carousel.vue'
import CollectionPlayer from '@canvas-builder/components/ui/CollectionPlayer/CollectionPlayer.vue'
import ContentGrid from '@canvas-builder/components/ui/ContentGrid/ContentGrid.vue'
import DataCharts from '@canvas-builder/components/ui/DataCharts/DataCharts.vue'
import DynamicDataTable from '@canvas-builder/components/ui/DynamicDataTable/DynamicDataTable.vue'
import Embeddable from '@canvas-builder/components/ui/Embeddable/Embeddable.vue'
import Grid from '@canvas-builder/components/ui/Grid/Grid.vue'
import GridItemTooltip from '@canvas-builder/components/ui/Grid/GridItemTooltip.vue'
import Html from '@canvas-builder/components/ui/Html/Html.vue'
import IFrame from '@canvas-builder/components/ui/IFrame/IFrame.vue'
import Link from '@canvas-builder/components/ui/Link/Link.vue'
import Multimedia from '@canvas-builder/components/ui/Multimedia/Multimedia.vue'
import Text from '@canvas-builder/components/ui/Text/Text.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'

import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasVisibility from '@canvas-builder/composables/useCanvasVisibility'
import { useComponentStyle } from '@canvas-builder/composables/useComponentStyle'
import useCanvasTheme from '@lib/apps/canvas-builder/composables/useCanvasTheme'
import useConfirmation from '@lib/composables/useConfirmation'
import useThemeVars from '@lib/composables/useThemeVars'

import LinkableComponent from '@canvas-builder/components/container/LinkableComponent/LinkableComponent.vue'
import PlaceholderComponent from '@canvas-builder/components/container/PlaceholderComponent/PlaceholderComponent.vue'
import useComponentPermissions from '@canvas-builder/composables/useComponentPermissions'
import { defaultWhenUsedInSection } from '@canvas-builder/constants'
import type { CanvasTheme } from '@canvas-builder/types/canvas'
import {
  ComponentTypes,
  GridLayoutTypes,
  type ComponentNode,
  type ComponentTrackingParams,
  type GridItemProps,
  type GridProps,
} from '@canvas-builder/types/canvas'
import { getSectionGlobalComponentSpacing, shouldDisplayPlaceholderComponent } from '@canvas-builder/util/canvas.util'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import { convertToPixels } from '@canvas-builder/util/print.util'
import { getExcessItemsIndexes } from '@lib/util/array'
import { hasPadding } from '@lib/util/style.util'
import { type CanvasRetrieve } from '@openapi'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'

const props = withDefaults(
  defineProps<{
    id: string
    data: GridProps
    children?: ComponentNode[]
    usedInSectionId?: CanvasRetrieve['id']
    when_used_in_section?: ComponentNode['when_used_in_section']
    visible?: ComponentNode['visible']
    print?: ComponentNode['print']
    tracking_id?: string
    style?: Record<string, any>
    theme_meta?: ComponentNode['theme_meta']
  }>(),
  {
    children: () => [],
    when_used_in_section: () => defaultWhenUsedInSection,
    usedInSectionId: '',
  },
)
const emit = defineEmits<{
  style: [s: Record<string, any>, id?: ComponentNode['id']]
  // NOTE: only works for 1 level of nesting, we would need to centralize if we want to go deeper
  nestedStyle: [s: Record<string, any>, id?: ComponentNode['id']]
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const { t } = useI18n()
const confirmation = useConfirmation()

const {
  activeCanvasSections,
  addComponentAtParentId,
  componentNodesById,
  componentSelectedMode,
  duplicate,
  isEditMode,
  isPrintDisplayMode,
  isViewOnlyMode,
  mode,
  onComponentEnter,
  onComponentExit,
  parentIdByNodeId,
  removeComponentById,
  resetChildrenOfNodeById,
  setAddMode,
  setComponentSelectedMode,
  setComponentSettingsMode,
  updateNodeDataById,
} = useCanvas()

const { getDerivedStyleFromFeatureFlags, getCssThemeMetaStyleOverrides } = useComponentStyle()

const isNestedGrid = computed(() => {
  const parentId = parentIdByNodeId.value[props.id]
  if (!parentId) return false

  const parentNode = componentNodesById.value[parentId]
  return parentNode.type === ComponentTypes.GridItem
})

const sectionTheme = computed(() => {
  return usedInSectionId.value ? (activeCanvasSections.value[usedInSectionId.value]?.theme as CanvasTheme) : undefined
})

const editableComponentGridStyle = computed(() => {
  const style = {
    ...getDerivedStyleFromFeatureFlags(props.style),
    ...getCssThemeMetaStyleOverrides(ComponentTypes.Grid, props.theme_meta, sectionTheme.value),
  }

  return omit(
    style,
    'paddingBottom',
    'paddingLeft',
    'paddingTop',
    'paddingRight',
    launchDarkly.value.enable_height_in_component_theming ? '' : 'height',
  )
})

function getEditableComponentgetGridItemStyle(c: ComponentNode) {
  return {
    ...getDerivedStyleFromFeatureFlags(omit(c.style, 'paddingBottom', 'paddingLeft', 'paddingTop', 'paddingRight')),
    ...getCssThemeMetaStyleOverrides(ComponentTypes.GridItem, c.theme_meta, sectionTheme.value),
  }
}

const gridStyle = computed(() => {
  const style = {
    ...getDerivedStyleFromFeatureFlags(props.style),
    ...getCssThemeMetaStyleOverrides(ComponentTypes.Grid, props.theme_meta, sectionTheme.value),
  }
  const baseStyle =
    !isEditMode.value || usedInSectionId.value
      ? style
      : pick(style, 'paddingBottom', 'paddingLeft', 'paddingTop', 'paddingRight')

  if (!launchDarkly.value.enable_height_in_component_theming || !style?.height) {
    return baseStyle
  }

  const styleObject: Record<string, any> = {
    ...baseStyle,
    maxHeight: isPrintDisplayMode.value
      ? '100%'
      : `calc(${style.height} - 20px - ${style?.paddingTop ?? '0px'} - ${style?.paddingBottom ?? '0px'})`,
    overflowY: 'auto',
  }

  if (isPrintDisplayMode.value && (!styleObject.height || !launchDarkly.value.enable_scale_content)) {
    styleObject.height = '100%'
  }

  if (
    isPrintDisplayMode.value &&
    !!launchDarkly.value.enable_scale_content &&
    props.print?.scale_to_fit_page &&
    pitcherSettings.value.default_pdf_page_dimensions?.width &&
    pitcherSettings.value.default_pdf_page_dimensions?.height
  ) {
    const widthPx = convertToPixels(pitcherSettings.value.default_pdf_page_dimensions.width)
    const heightPx = convertToPixels(pitcherSettings.value.default_pdf_page_dimensions.height)
    if (widthPx && heightPx && widthPx > 0 && heightPx > 0) {
      styleObject.height = `${heightPx}px`
      styleObject.width = `${widthPx}px`
    }
  }

  return styleObject
})

function getGridItemStyle(c: ComponentNode) {
  return {
    ...getDerivedStyleFromFeatureFlags(c.style),
    ...getCssThemeMetaStyleOverrides(ComponentTypes.GridItem, c.theme_meta, sectionTheme.value),
  }
}

const { shouldRenderComponentWrapper } = useCanvasVisibility()

const { globalComponentSpacing } = useCanvasTheme()
const componentMap = {
  Carousel,
  CollectionPlayer,
  ContentGrid,
  DataCharts,
  DynamicDataTable,
  Embeddable,
  Grid,
  Html,
  IFrame,
  Link,
  Multimedia,
  Text,
} as Record<ComponentTypes, any>
const themeVars = useThemeVars()
const { id, usedInSectionId, when_used_in_section } = toRefs(props)
const componentPermissions = useComponentPermissions({
  id,
  usedInSectionId,
  when_used_in_section,
})

const GRID_OPTIONS = [
  {
    label: t('canvasUI.canvasBuilder.grid.layoutNames.one'),
    key: GridLayoutTypes.ONE,
    icon: () => h(CIcon, { icon: 'fas fa-align-justify', size: '18px' }),
  },
  {
    label: t('canvasUI.canvasBuilder.grid.layoutNames.twoEqual'),
    key: GridLayoutTypes.TWO_EQUAL,
    icon: () => h(CIcon, { icon: 'fas fa-columns', size: '18px' }),
  },
  {
    label: t('canvasUI.canvasBuilder.grid.layoutNames.twoLeft'),
    key: GridLayoutTypes.TWO_LEFT,
    icon: () => h(CIcon, { icon: 'fas fa-align-left', size: '18px' }),
  },
  {
    label: t('canvasUI.canvasBuilder.grid.layoutNames.twoRight'),
    key: GridLayoutTypes.TWO_RIGHT,
    icon: () => h(CIcon, { icon: 'fas fa-align-right', size: '18px' }),
  },
  {
    label: t('canvasUI.canvasBuilder.grid.layoutNames.threeEqual'),
    key: GridLayoutTypes.THREE_EQUAL,
    icon: () => h(CIcon, { icon: 'fas fa-table', size: '18px' }),
  },
  {
    label: t('canvasUI.canvasBuilder.grid.layoutNames.fourEqual'),
    key: GridLayoutTypes.FOUR_EQUAL,
    icon: () => h(CIcon, { icon: 'fas fa-th', size: '18px' }),
  },
]

function displayDownsizingConfirmation(spans: number[], columns: number, variant: GridLayoutTypes) {
  confirmation.danger({
    hasExit: false,
    title: t('canvasUI.canvasBuilder.grid.deleteDialog.title'),
    content: t('canvasUI.canvasBuilder.grid.deleteDialog.content'),
    confirmText: t('canvasUI.canvasBuilder.grid.deleteDialog.confirm'),
    cancelText: t('canvasUI.canvasBuilder.grid.deleteDialog.cancel'),
    onConfirm: () => updateContext(spans, columns, variant),
  })
}

function getColumnSpans(data: GridProps): number[] {
  const spans = {
    [GridLayoutTypes.ONE]: [2],
    [GridLayoutTypes.TWO_EQUAL]: [1, 1],
    [GridLayoutTypes.TWO_LEFT]: [2, 1],
    [GridLayoutTypes.TWO_RIGHT]: [1, 2],
    [GridLayoutTypes.THREE_EQUAL]: [1, 1, 1],
    [GridLayoutTypes.FOUR_EQUAL]: [1, 1, 1, 1],
  }

  return spans[data.layout_type] ?? []
}

function getComponentBindings(c: ComponentNode) {
  const omitList = ['type']
  if (c.type !== ComponentTypes.Grid) omitList.push('children')

  return {
    ...omit(c, omitList),
    style: {
      ...c?.style,
      marginBottom: undefined,
    } satisfies Record<string, any>,
  }
}

function getComponentMarginBottom(style?: Record<string, any>) {
  return (
    style?.marginBottom ??
    (usedInSectionId.value
      ? getSectionGlobalComponentSpacing(activeCanvasSections.value[usedInSectionId.value])
      : `${globalComponentSpacing.value}px`)
  )
}

function getSpan(c: ComponentNode) {
  return (c?.data as GridItemProps)?.span ?? 1
}

function getTooltips(c: ComponentNode) {
  return (c?.data as GridItemProps)?.tooltips ?? []
}

function onMouseDown(e: MouseEvent, i: ComponentNode) {
  if (e.button !== 0) return
  componentSelectedMode.value && componentSelectedMode.value?.id === i.id
    ? setAddMode(ComponentTypes.GridItem, i.id)
    : setComponentSelectedMode(i.id)
}

function onVariantChange(selection?: DropdownOption['key']) {
  const variant = selection as GridLayoutTypes
  let columns
  switch (variant) {
    case GridLayoutTypes.TWO_LEFT:
    case GridLayoutTypes.TWO_RIGHT:
    case GridLayoutTypes.THREE_EQUAL:
      columns = 3
      break
    case GridLayoutTypes.FOUR_EQUAL:
      columns = 4
      break
    default:
      columns = 2
      break
  }

  const existingColumnSpans = getColumnSpans(props.data)
  const newColumnSpans = getColumnSpans({ columns, layout_type: variant })
  const indexes = getExcessItemsIndexes(existingColumnSpans, newColumnSpans)
  const areThereChildrenWithData = props.children
    .filter((item, index) => indexes.includes(index))
    .some((item) => item.children?.length)

  if (existingColumnSpans.length > newColumnSpans.length && areThereChildrenWithData)
    displayDownsizingConfirmation(newColumnSpans, columns, variant)
  else updateContext(newColumnSpans, columns, variant)
}

function updateContext(spans: number[], columns: number, variant: GridLayoutTypes) {
  updateNodeDataById(props.id, { columns, layout_type: variant })

  const existingChildren = [...(props.children || [])]
  resetChildrenOfNodeById(props.id)

  spans.forEach((span, index) => {
    const existingChild = existingChildren[index]
    const style = existingChild?.style
    const theme_meta = existingChild?.theme_meta
    const tooltips = (existingChild?.data as GridItemProps)?.tooltips

    const data: GridItemProps = { span }
    if (tooltips) {
      data.tooltips = tooltips
    }

    const extraNodeData: Partial<ComponentNode> = {}
    if (style) {
      extraNodeData.style = style
    }
    if (theme_meta) {
      extraNodeData.theme_meta = theme_meta
    }

    addComponentAtParentId(ComponentTypes.GridItem, props.id, null, data, existingChild?.children || [], extraNodeData)
  })
}

function onElementVisibility(isVisible: boolean, c: ComponentNode) {
  const params: ComponentTrackingParams = { trackable: false }

  if (isVisible) onComponentEnter.value?.(c, params)
  else onComponentExit.value?.(c, params)
}

function getGridItemChildStyle(
  childComponent: ComponentNode,
  childIndex: number,
  allChildren: ComponentNode[],
  shouldFilter: boolean = false,
): Record<string, any> {
  const children = shouldFilter
    ? allChildren.filter((child) => shouldRenderComponentWrapper(child, usedInSectionId.value))
    : allChildren

  const childrenLength = children.length
  const isLastChild = childrenLength > 0 && childIndex === childrenLength - 1
  const isOnlyChild = childrenLength === 1

  const hasIntuitiveSpacing = launchDarkly.value.enable_more_intuitive_component_spacing
  const shouldRemoveMargin = hasIntuitiveSpacing ? isLastChild || isOnlyChild : isPrintDisplayMode.value && isLastChild

  return shouldRemoveMargin
    ? {}
    : {
        marginBottom: getComponentMarginBottom(childComponent?.style),
      }
}
</script>

<template>
  <PlaceholderComponent v-if="shouldDisplayPlaceholderComponent(isEditMode, mode, visible)" :name="tracking_id" />
  <EditableComponent
    v-else-if="isEditMode && !isViewOnlyMode && componentPermissions.hasSomethingEditable"
    :id="id"
    :duplicate="componentPermissions.canDuplicate"
    :remove="componentPermissions.canRemove"
    :selected="componentSelectedMode && componentSelectedMode?.id === id"
    :settings="!usedInSectionId"
    :stylable="!usedInSectionId"
    :style="editableComponentGridStyle"
    :variants="
      componentPermissions.canEdit
        ? {
            title: t('canvasUI.canvasBuilder.grid.selectLayout'),
            options: GRID_OPTIONS,
            selected: data?.layout_type ?? GridLayoutTypes.TWO_EQUAL,
          }
        : undefined
    "
    @duplicate="duplicate(id)"
    @remove="removeComponentById(id)"
    @select="setComponentSelectedMode(id)"
    @settings="setComponentSettingsMode(id)"
    @style="
      (event) => {
        isNestedGrid ? emit('nestedStyle', event, id) : emit('style', event)
      }
    "
    @variant-change="onVariantChange"
  >
    <NGrid
      class="items-start"
      :cols="data?.columns ?? children.length"
      item-responsive
      :style="gridStyle"
      x-gap="32"
      y-gap="32"
    >
      <NGridItem
        v-for="i in children"
        :key="i.id"
        class="relative"
        :class="{ 'pa-2': !hasPadding(i.style) }"
        :span="getSpan(i)"
        :style="getGridItemStyle(i)"
      >
        <GridItemTooltip v-for="(tooltip, tooltipIdx) in getTooltips(i)" :key="tooltipIdx" :tooltip="tooltip" />
        <PlaceholderComponent
          v-if="shouldDisplayPlaceholderComponent(isEditMode, mode, i.visible)"
          :name="i.tracking_id"
        />
        <EditableComponent
          v-else
          :id="i.id"
          :add="componentPermissions.canEdit"
          class="pa-2"
          :selected="componentSelectedMode && componentSelectedMode?.id === i.id"
          settings
          stylable
          :style="getEditableComponentgetGridItemStyle(i)"
          @add="setAddMode(ComponentTypes.GridItem, i.id)"
          @select="setComponentSelectedMode(i.id)"
          @settings="setComponentSettingsMode(i.id)"
          @style="
            (event) => {
              isNestedGrid ? emit('nestedStyle', event, i.id) : emit('style', event, i.id)
            }
          "
        >
          <div
            v-if="!i.children?.length"
            class="grid-item-placeholder flex justify-center items-center b-rounded-2 cursor-pointer min-h-[150px]"
            @mousedown.stop="onMouseDown($event, i)"
          >
            <CButton circle secondary size="large" strong>
              <template #icon>
                <CIcon icon="plus-large" size="18" />
              </template>
            </CButton>
          </div>
          <template v-else>
            <div
              v-for="(j, idx) in i.children"
              :key="j.id"
              v-element-visibility="(isVisible: boolean) => onElementVisibility(isVisible, j)"
              class="component-wrapper relative"
              :class="{ 'mt-2': !!idx }"
              :style="getGridItemChildStyle(j, idx, i.children ?? [])"
            >
              <LinkableComponent :id="j.id" :used-in-section-id="usedInSectionId">
                <Component
                  :is="componentMap[j.type]"
                  v-bind="getComponentBindings(j)"
                  :data-component-id="j.id"
                  :used-in-section-id="usedInSectionId"
                  @nested-style="(style: Record<string, any>, id: ComponentNode['id']) => emit('style', style, id)"
                  @style="
                    (style: Record<string, any>) => {
                      isNestedGrid ? emit('nestedStyle', style, j.id) : emit('style', style, j.id)
                    }
                  "
                />
              </LinkableComponent>
            </div>
          </template>
        </EditableComponent>
      </NGridItem>
    </NGrid>
  </EditableComponent>
  <NGrid
    v-else
    class="items-start"
    :cols="data?.columns ?? children.length"
    item-responsive
    :style="gridStyle"
    x-gap="32"
    y-gap="32"
  >
    <NGridItem v-for="i in children" :key="i.id" class="relative" :span="getSpan(i)" :style="getGridItemStyle(i)">
      <GridItemTooltip v-for="(tooltip, tooltipIdx) in getTooltips(i)" :key="tooltipIdx" :tooltip="tooltip" />
      <template
        v-for="(j, idx) in (i.children ?? []).filter((child) => shouldRenderComponentWrapper(child, usedInSectionId))"
        :key="j.id"
      >
        <div
          v-element-visibility="(isVisible: boolean) => onElementVisibility(isVisible, j)"
          class="component-wrapper relative"
          :style="getGridItemChildStyle(j, idx, i.children ?? [], true)"
        >
          <LinkableComponent :id="j.id" :used-in-section-id="usedInSectionId">
            <Component
              :is="componentMap[j.type]"
              :key="j.id"
              :data-component-id="j.id"
              v-bind="getComponentBindings(j)"
              :used-in-section-id="usedInSectionId"
            />
          </LinkableComponent>
        </div>
      </template>
    </NGridItem>
  </NGrid>
</template>

<style scoped>
.grid-item-placeholder {
  background-color: v-bind('themeVars.primary6');
}
</style>
